import { FeedOrderSelector } from "@/ui/feed-order-selector";
import {
	createFileRoute,
	useParams,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import type { FeedOrdering } from "./__generated__/comunidade_feedQuery.graphql";
import {
	graphql,
	useLazyLoadQuery,
	usePaginationFragment,
	fetchQuery,
} from "react-relay";
import type { spaceFeedQuery } from "./__generated__/spaceFeedQuery.graphql";
import type { spaceHeadQuery } from "./__generated__/spaceHeadQuery.graphql";
import { CreatePostButton } from "@/ui/create-post-button";
import { PostPreview } from "@/ui/post-preview";
import type { spaceFeedFragmentQuery } from "./__generated__/spaceFeedFragmentQuery.graphql";
import type { spaceFeedFragment$key } from "./__generated__/spaceFeedFragment.graphql";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import { useEffect, useTransition } from "react";
import { Spinner } from "@/components/ui/spinner";
import { Icon } from "@/components/icon";
import { SpacePaywall } from "@/ui/space-paywall";
import { environment } from "@/relay/environment";

export const Route = createFileRoute(
	"/_visibility/_topnav/_tabs/_sidebar/comunidade/s/$space",
)({
	component: RouteComponent,
	loader: ({ context }) => {
		return { club: context.club };
	},
	pendingComponent: () => {
		return (
			<div className="flex flex-col w-full gap-6">
				<CreatePostButton />
				<FeedOrderSelector />

				<div className="flex items-center justify-center py-12">
					<Spinner />
				</div>
			</div>
		);
	},
	pendingMinMs: 100,
	head: async ({ params: { space }, loaderData }) => {
		if (!loaderData?.club) {
			return {};
		}

		const activeDomain = loaderData.club.domains.find((d) => d.enabled)?.domain;
		const clubUrl = activeDomain
			? `https://${activeDomain}`
			: "https://scarf.club";
		const canonicalUrl = `${clubUrl}/comunidade/s/${space}`;

		const data = await fetchQuery<spaceHeadQuery>(
			environment,
			graphql`
	    query spaceHeadQuery($club: String!, $space: String!) {
	        club(slug: $club) {
						name
	          space(slug: $space) {
	            slug
	            name
							visibility
	          }
	        }
	    }
	  `,
			{ club: loaderData.club.slug!, space },
		).toPromise();

		if (!data?.club.space || data.club.space.visibility !== "PUBLIC") {
			return {
				meta: [
					{
						name: "robots",
						content: "noindex,nofollow",
					},
				],
			};
		}

		const structuredData = {
			"@context": "https://schema.org",
			"@type": "WebSite",
			name: data.club.space.name,
			url: canonicalUrl,
			isPartOf: {
				"@type": "WebSite",
				name: data.club.name,
				url: clubUrl,
			},
		};

		return {
			meta: [
				{
					title: `${data.club.space.name} | ${data.club.name}`,
				},
				{
					name: "canonical",
					content: canonicalUrl,
				},
				{
					property: "og:title",
					content: `${data.club.space.name} | ${data.club.name}`,
				},
				{
					property: "og:url",
					content: canonicalUrl,
				},
			],
			links: [
				{
					rel: "canonical",
					href: canonicalUrl,
				},
			],
			scripts: [
				{
					type: "application/ld+json",
					innerHTML: JSON.stringify(structuredData),
				},
			],
		};
	},
});

function RouteComponent() {
	const { space } = useParams({ from: Route.id });
	const { club } = useRouteContext({ from: "__root__" });
	const { order } = useSearch({
		from: "/_visibility/_topnav/_tabs/_sidebar/comunidade",
	});

	const [isPending, startTransition] = useTransition();

	const [ref, entry] = useIntersectionObserver({
		threshold: 0,
		root: null,
		rootMargin: "0px",
	});

	const filter: FeedOrdering =
		order === "trending"
			? "TRENDING"
			: order === "oldest"
				? "OLDEST"
				: "NEWEST";

	const data = useLazyLoadQuery<spaceFeedQuery>(
		graphql`
    query spaceFeedQuery($slug: String!, $space: String!, $order: FeedOrdering!) {
      club(slug: $slug) {
        space(slug: $space) {
          slug
          isMember
          visibility

          ...spacePaywall_space
          ...createPostButtonFragment
          ...spaceFeedFragment @arguments(order: $order, club: $slug)
        }
      }
    }
  `,
		{ slug: club?.slug, order: filter, space },
	);
	const {
		data: { feed },
		loadNext,
		refetch,
	} = usePaginationFragment<spaceFeedFragmentQuery, spaceFeedFragment$key>(
		graphql`
	  fragment spaceFeedFragment on Space
		@refetchable(queryName: "spaceFeedFragmentQuery")
		@argumentDefinitions(
    	first: { type: "Int", defaultValue: 10 },
    	cursor: { type: "String" }
      order: { type: "FeedOrdering", defaultValue: NEWEST }
      club: { type: "String!" }
		)
		{
			feed(after: $cursor, first: $first, orderBy: $order, ignoreFixed: false)
			@connection(key: "spaceFeedFragment__feed")
		  {
    			edges {
     			  node {
                ... on Post {
     			    id
       					...postPreviewFragment @arguments(club: $club)
        				}
        				__typename
       			}
    			}
          pageInfo {
            hasNextPage
          }
			}
		}
	`,
		data.club.space,
	);

	useEffect(() => {
		if (!isPending && entry?.isIntersecting && feed.pageInfo.hasNextPage) {
			startTransition(() => {
				loadNext(10);
			});
		}
	}, [entry?.isIntersecting, isPending, loadNext, feed.pageInfo.hasNextPage]);

	if (
		!club.permissions.length &&
		!data.club.space.isMember &&
		(data.club.space.visibility === "PRIVATE" ||
			data.club.space.visibility === "SECRET")
	) {
		return <SpacePaywall space={data.club.space} />;
	}

	return (
		<div className="flex flex-col flex-1 w-full gap-6 xl:last:w-full xl:not-last:max-w-[calc(1000px-340px-1.5rem)] max-xl:w-full">
			<CreatePostButton space={data.club.space} />
			<FeedOrderSelector />

			{feed.edges?.filter(Boolean).length ? (
				<>
					{feed.edges.filter(Boolean).map(({ node }) => {
						if (node?.__typename === "Post") {
							return (
								<PostPreview
									key={node.id}
									post={node}
									showSpaceFeedPin
									onUpdate={() => {
										refetch({ order: filter }, { fetchPolicy: "network-only" });
									}}
								/>
							);
						}

						return null;
					})}

					<div ref={ref} className="size-1" />

					{isPending && (
						<div className="flex items-center justify-center">
							<Spinner />
						</div>
					)}
				</>
			) : (
				<div className="flex flex-col items-center justify-center gap-6">
					<Icon name="CircleSlash" className="size-16" />

					<span>Ainda não tem nada aqui :/</span>
				</div>
			)}
		</div>
	);
}
